import React, { useEffect, useMemo } from 'react';
import { Button, Form, Modal } from 'antd';
import { useData } from '../../hooks/App/useData';
import { ApiEndpoints } from '../../data/ApiEndpoints';
import { Translated } from '../UI/Core';
import { SearchSelect } from '../UI/Select/SearchSelect';
import { generatePDF } from '../../utils/Pdf/generatePdf';

// Models
import { PersonType } from '../../models/enums/PersonType';

// Hooks
import { usePassTemplateSelect } from '../../hooks/PassTemplates/usePassTemplateSelect';
import { SelectedOption } from '../../models/SelectedOption';
import { PassTemplatePerson } from '../../models/PassTemplates/PassTemplatePerson';
import { useTenantData } from '../../hooks/App/useTenantData';

interface InfoModalProps {
  isModalOpen: boolean;
  onClose: () => void;
  personType?: PersonType;
  passTemplatePerson?: PassTemplatePerson;
}

const PrintCardModal: React.FC<InfoModalProps> = ({ isModalOpen, onClose, personType, passTemplatePerson }) => {
  const [form] = Form.useForm();
  const { data: passTemplate, fetch: fetchPassTemplate } = useData(ApiEndpoints.passTemplates.detail, null);
  const { data: company, fetch: fetchCompany } = useData(ApiEndpoints.company.detail, null);
  const { getTenantDataProps } = useTenantData();

  useEffect(() => {
    if (personType === PersonType.Employee) {
      const { tenantData } = getTenantDataProps();
      const companyId = tenantData?.Tenants.find((tenant) => tenant.Id === tenantData.Id)?.CompanyId;
      fetchCompany({ id: companyId });
    }
  }, [fetchCompany, getTenantDataProps, personType]);

  const newPassTemplatePerson = useMemo(() => {
    if (personType === PersonType.Employee) {
      return {
        ...passTemplatePerson,
        Department: company?.Name,
      };
    }
    return passTemplatePerson;
  }, [passTemplatePerson, personType, company]);

  const passTemplateOptions = usePassTemplateSelect(undefined, personType);

  const selectOptionsProvider = (initialValue?: Array<SelectedOption>) => {
    return {
      ...passTemplateOptions,
      initialValue,
    };
  };

  const validateForm = async (): Promise<boolean> => {
    try {
      await form.validateFields();
      return true;
    } catch {
      return false;
    }
  };

  const handlePrint = async () => {
    const isValid = await validateForm();

    if (!isValid || passTemplate === null) {
      return;
    }

    if (newPassTemplatePerson) {
      await generatePDF(passTemplate, newPassTemplatePerson as PassTemplatePerson);
    }
    onClose();
  };

  const handleValueChange = async (id: unknown) => {
    await fetchPassTemplate({ id });
  };

  return (
    <Modal
      title={<Translated id="printCard.modal.title" />}
      centered
      open={isModalOpen}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          <Translated id="form.cancelButton" />
        </Button>,
        <Button key="ok" type="primary" onClick={handlePrint}>
          <Translated id="printCard.modal.button" />
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" name="printCardModalForm">
        <Form.Item
          name="PassTemplate"
          label={<Translated id="printCard.select.passTemplate" />}
          rules={[{ required: true, message: 'Pass template is required' }]}
        >
          <SearchSelect
            selectOptionsProvider={selectOptionsProvider}
            onValueChange={(value: unknown) => {
              form.setFieldsValue({ PassTemplate: value });
              handleValueChange(value);
            }}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PrintCardModal;
